// Typed wrappers for /api/v2/admin/users, /api/v2/admin/online-sessions, and /api/v2/admin/roles.
// Endpoints not yet in openapi.yaml use raw fetch; fully-typed endpoints use apiClient.
import { apiClient } from "@/lib/api/client";
import { withCsrf } from "@/lib/api/csrf";
import { ApiError, unwrap } from "@/lib/api/error";
import type {
  AdminProfileView,
  AdminRobotIntel,
  AdminRobotIntelPatch,
  AdminRoleItem,
  AdminRoleList,
  AdminTradeListResponse,
  AdminTradeStatsResponse,
  AdminTradesQueryParams,
  AdminUserActivityResponse,
  AdminUserList,
  AdminUserPatch,
  AdminUserRow,
  AdminUserTradeDetail,
  AdminUsersListParams,
  AssignRoleBody,
  DepositLimitPatchBody,
  DepositLimitPatchResponse,
  KickResult,
  ListSessionsParams,
  OnlineSessionList,
  ProfileExtraRevokeResponse,
  PurchasesQueryParams,
  ReconcileDepositTotalsResponse,
  TokenAdjustmentBody,
  TokenAdjustmentResponse,
  TokenBonusCreateBody,
  TokenBonusCreateResponse,
  TokensDetailQueryParams,
  UserActivityQueryParams,
  UserMinDepositPatchBody,
  UserPurchasesResponse,
  UserRobotLevelPatchBody,
  UserRobotLevelPatchResponse,
  UserTokensDetailResponse,
} from "./api-contracts";

export * from "./api-contracts";

async function adminFetch<T>(url: string, opts?: RequestInit): Promise<T> {
  const res = await withCsrf(fetch)(url, { credentials: "include", ...opts });
  if (!res.ok) {
    const body = await responseErrorBody(res);
    throw new ApiError(
      res.status,
      body,
      safeAdminUsersErrorMessage(res.status, body),
    );
  }
  return res.json() as Promise<T>;
}

function safeAdminUsersErrorMessage(status: number, body: unknown): string {
  if (status === 422 && body && typeof body === "object") {
    const detail = (body as Record<string, unknown>)["detail"];
    if (typeof detail === "string" && detail.trim()) return detail;
  }
  return "adminFetch failed";
}

async function responseErrorBody(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export async function fetchAdminUsers(
  params: AdminUsersListParams,
): Promise<AdminUserList> {
  const { data, error } = await apiClient.GET("/api/v2/admin/users", {
    params: { query: params },
  });
  if (error || !data)
    throw new Error(`fetchAdminUsers failed: ${JSON.stringify(error)}`);
  return data;
}

function normalizeReconcileEmail(email: string): string {
  const normalized = email.trim();
  if (normalized.length > 0) return normalized;
  throw new Error(
    `reconcile email expected non-empty string got=${JSON.stringify(email)}`,
  );
}

export async function reconcileDepositTotalsForEmail(
  email: string,
): Promise<ReconcileDepositTotalsResponse> {
  return adminFetch<ReconcileDepositTotalsResponse>(
    "/api/v2/admin/deposit-totals/reconcile",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: normalizeReconcileEmail(email) }),
    },
  );
}

export async function fetchAdminUser(userId: string): Promise<AdminUserRow> {
  const { data, error } = await apiClient.GET("/api/v2/admin/users/{user_id}", {
    params: { path: { user_id: userId } },
  });
  if (error || !data)
    throw new Error(
      `fetchAdminUser failed id=${userId}: ${JSON.stringify(error)}`,
    );
  return data;
}

export async function patchAdminUser(
  userId: string,
  body: AdminUserPatch,
): Promise<AdminUserRow> {
  const { data, error } = await apiClient.PATCH(
    "/api/v2/admin/users/{user_id}",
    {
      params: { path: { user_id: userId } },
      body,
    },
  );
  if (error || !data)
    throw new Error(
      `patchAdminUser failed id=${userId}: ${JSON.stringify(error)}`,
    );
  return data;
}

export async function fetchAdminProfileView(
  userId: string,
): Promise<AdminProfileView> {
  const { data, error } = await apiClient.GET(
    "/api/v2/admin/users/{user_id}/profile-view",
    {
      params: { path: { user_id: userId } },
    },
  );
  if (error || !data)
    throw new Error(
      `fetchAdminProfileView failed id=${userId}: ${JSON.stringify(error)}`,
    );
  return data;
}

export async function fetchAdminOnlineSessions(
  params: ListSessionsParams,
): Promise<OnlineSessionList> {
  const query = new URLSearchParams({
    limit: String(params.limit ?? 50),
    offset: String(params.offset ?? 0),
  });
  return adminFetch<OnlineSessionList>(
    `/api/v2/admin/online-sessions?${query}`,
  );
}

export async function kickSession(sessionId: string): Promise<KickResult> {
  return adminFetch<KickResult>(`/api/v2/admin/online-sessions/${sessionId}`, {
    method: "DELETE",
  });
}

export async function fetchAdminRoles(
  params: ListSessionsParams,
): Promise<AdminRoleList> {
  const query = new URLSearchParams({
    limit: String(params.limit ?? 50),
    offset: String(params.offset ?? 0),
  });
  return adminFetch<AdminRoleList>(`/api/v2/admin/roles?${query}`);
}

export async function assignRole(body: AssignRoleBody): Promise<AdminRoleItem> {
  return adminFetch<AdminRoleItem>("/api/v2/admin/roles", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function fetchUserRobotIntel(
  userId: string,
): Promise<AdminRobotIntel> {
  return adminFetch<AdminRobotIntel>(
    `/api/v2/admin/users/${userId}/robot-intel`,
  );
}

export async function patchUserRobotIntel(
  userId: string,
  body: AdminRobotIntelPatch,
): Promise<AdminRobotIntel> {
  return adminFetch<AdminRobotIntel>(
    `/api/v2/admin/users/${userId}/robot-intel`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function patchUserDepositLimit(
  userId: string,
  body: DepositLimitPatchBody,
): Promise<DepositLimitPatchResponse> {
  return adminFetch<DepositLimitPatchResponse>(
    `/api/v2/admin/users/${userId}/deposit-limit`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function patchUserMinDeposit(
  userId: string,
  body: UserMinDepositPatchBody,
): Promise<DepositLimitPatchResponse> {
  return adminFetch<DepositLimitPatchResponse>(
    `/api/v2/admin/users/${userId}/min-deposit`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function patchAdminUserRobotLevel(
  userId: string,
  body: UserRobotLevelPatchBody,
): Promise<UserRobotLevelPatchResponse> {
  return adminFetch<UserRobotLevelPatchResponse>(
    `/api/v2/admin/users/${userId}/robot-level`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

// 07/08/2026: grantAdminUserTokenPro removida. Zero callers e
// POST /api/v2/admin/users/{id}/tokens/grant não existe no backend — o
// caminho vivo de tokens é adjustUserTokens (POST .../tokens-adjustments).

export async function fetchPurchases(
  userId: string,
  params: PurchasesQueryParams = {},
): Promise<UserPurchasesResponse> {
  const { data, error } = await apiClient.GET(
    "/api/v2/admin/users/{user_id}/purchases",
    { params: { path: { user_id: userId }, query: params } },
  );
  if (error || !data)
    throw new Error(
      `fetchPurchases failed id=${userId}: ${JSON.stringify(error)}`,
    );
  return data;
}

export async function fetchTokensDetail(
  userId: string,
  params: TokensDetailQueryParams = {},
): Promise<UserTokensDetailResponse> {
  const { data, error } = await apiClient.GET(
    "/api/v2/admin/users/{user_id}/tokens-detail",
    { params: { path: { user_id: userId }, query: params } },
  );
  if (error || !data)
    throw new Error(
      `fetchTokensDetail failed id=${userId}: ${JSON.stringify(error)}`,
    );
  return data;
}

export async function adjustUserTokens(
  userId: string,
  body: TokenAdjustmentBody,
): Promise<TokenAdjustmentResponse> {
  return adminFetch<TokenAdjustmentResponse>(
    `/api/v2/admin/users/${userId}/tokens-adjustments`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function createUserTokenBonus(
  userId: string,
  body: TokenBonusCreateBody,
): Promise<TokenBonusCreateResponse> {
  const result = await apiClient.POST<TokenBonusCreateResponse>(
    "/api/v2/admin/users/{user_id}/token-bonuses",
    { params: { path: { user_id: userId } }, body },
  );
  return unwrap(result);
}

/** Stats win/loss do período com gale agrupado (operador 09/07/2026). */
export async function fetchUserTradeStats(
  params: Omit<AdminTradesQueryParams, "status" | "limit" | "offset"> = {},
): Promise<AdminTradeStatsResponse> {
  const { data, error } = await apiClient.GET("/api/v2/admin/trades/stats", {
    params: { query: params },
  });
  if (error || !data)
    throw new Error(`fetchUserTradeStats failed: ${JSON.stringify(error)}`);
  return data;
}

export async function fetchUserTrades(
  params: AdminTradesQueryParams = {},
): Promise<AdminTradeListResponse> {
  const { data, error } = await apiClient.GET("/api/v2/admin/trades", {
    params: { query: params },
  });
  if (error || !data)
    throw new Error(`fetchUserTrades failed: ${JSON.stringify(error)}`);
  return data;
}

export async function fetchUserTradeDetail(
  userId: string,
  tradeId: string,
): Promise<AdminUserTradeDetail> {
  const { data, error } = await apiClient.GET(
    "/api/v2/admin/users/{user_id}/trades/{trade_id}",
    { params: { path: { user_id: userId, trade_id: tradeId } } },
  );
  if (error || !data)
    throw new Error(
      `fetchUserTradeDetail failed user=${userId} trade=${tradeId}: ${JSON.stringify(error)}`,
    );
  return data;
}

export async function fetchUserActivity(
  userId: string,
  params: UserActivityQueryParams = {},
): Promise<AdminUserActivityResponse> {
  const { data, error } = await apiClient.GET(
    "/api/v2/admin/users/{user_id}/activity",
    { params: { path: { user_id: userId }, query: params } },
  );
  if (error || !data)
    throw new Error(
      `fetchUserActivity failed id=${userId}: ${JSON.stringify(error)}`,
    );
  return data;
}

export async function revokeProfileExtra(
  userId: string,
  extraId: string,
): Promise<ProfileExtraRevokeResponse> {
  return adminFetch<ProfileExtraRevokeResponse>(
    `/api/v2/admin/users/${encodeURIComponent(userId)}/extras/${encodeURIComponent(extraId)}`,
    { method: "DELETE" },
  );
}

/** Corrige a moeda de UMA conta (0088). Auditado no backend. */
export async function patchUserBrokerCurrency(
  userId: string,
  brokerCurrency: "BRL" | "USD",
): Promise<{ success: boolean; userId: string; brokerCurrency: string }> {
  return adminFetch(`/api/v2/admin/users/${userId}/broker-currency`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ brokerCurrency }),
  });
}
